import { Controller } from "@hotwired/stimulus"
import { uploadCv, confirmCv } from "../modules/salary_lab/cv_upload_api"

const MAX_FILE_SIZE = 10 * 1024 * 1024

export default class extends Controller {
  static targets = [
    "dropZone", "fileInput", "fileName", "statusMessage",
    "extractedData", "consentCheckbox", "confirmButton",
    "uploadButton", "uploadSection", "reviewSection", "successSection"
  ]

  static values = {
    uploadUrl: String,
    confirmUrl: String
  }

  connect() {
    this.extractedFields = {}
  }

  openFilePicker() {
    this.fileInputTarget.click()
  }

  selectFile(event) {
    const file = event.target.files[0]
    if (!file) return
    this._setFile(file)
  }

  handleDragOver(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.add("border-cyan-500", "bg-cyan-50")
  }

  handleDragLeave(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-cyan-500", "bg-cyan-50")
  }

  handleDrop(event) {
    event.preventDefault()
    this.dropZoneTarget.classList.remove("border-cyan-500", "bg-cyan-50")

    const file = event.dataTransfer.files[0]
    if (!file) return
    this._setFile(file)
  }

  async upload() {
    if (!this.selectedFile) return

    this._showStatus("Uploading and analysing your CV...", "info")
    this._setUploading(true)

    const result = await uploadCv(this.uploadUrlValue, this.selectedFile)

    this._setUploading(false)

    if (result.error) {
      this._showStatus(result.error, "error")
      return
    }

    this.extractedFields = result.extracted_data
    this._renderExtractedData(result.extracted_data)
    this._showStatus(`Extracted ${result.field_count} fields from your CV.`, "success")
    this.uploadSectionTarget.classList.add("hidden")
    this.reviewSectionTarget.classList.remove("hidden")
  }

  removeField(event) {
    const field = event.currentTarget.dataset.field
    delete this.extractedFields[field]
    const card = event.currentTarget.closest("[data-field-card]")
    if (card) card.remove()
  }

  editField(event) {
    const field = event.currentTarget.dataset.field
    const card = event.currentTarget.closest("[data-field-card]")
    const display = card.querySelector("[data-field-display]")
    const edit = card.querySelector("[data-field-edit]")

    if (edit.classList.contains("hidden")) {
      display.classList.add("hidden")
      edit.classList.remove("hidden")
      edit.querySelector("input, textarea").focus()
    } else {
      const input = edit.querySelector("input, textarea")
      const value = input.value.trim()
      if (value) {
        this.extractedFields[field] = value
        display.textContent = value
      }
      edit.classList.add("hidden")
      display.classList.remove("hidden")
    }
  }

  toggleConfirm() {
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = !this.consentCheckboxTarget.checked
    }
  }

  async confirmSave() {
    if (!this.consentCheckboxTarget.checked) return

    this.confirmButtonTarget.disabled = true
    this.confirmButtonTarget.textContent = "Saving..."

    const result = await confirmCv(
      this.confirmUrlValue,
      this.extractedFields,
      true
    )

    if (result.error) {
      this._showStatus(result.error, "error")
      this.confirmButtonTarget.disabled = false
      this.confirmButtonTarget.textContent = "Save to My Profile"
      return
    }

    this.reviewSectionTarget.classList.add("hidden")
    this.successSectionTarget.classList.remove("hidden")
    this._showStatus(
      `Saved ${result.memories_created} career facts to your profile.`,
      "success"
    )
  }

  clear() {
    this.selectedFile = null
    this.extractedFields = {}
    this.fileInputTarget.value = ""
    if (this.hasFileNameTarget) this.fileNameTarget.textContent = ""
    if (this.hasUploadButtonTarget) this.uploadButtonTarget.classList.add("hidden")
    this.uploadSectionTarget.classList.remove("hidden")
    this.reviewSectionTarget.classList.add("hidden")
    this.successSectionTarget.classList.add("hidden")
    this._clearStatus()
  }

  // Private

  _setFile(file) {
    if (file.type !== "application/pdf") {
      this._showStatus("Only PDF files are accepted.", "error")
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      this._showStatus("File must be under 10MB.", "error")
      return
    }

    this.selectedFile = file
    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = file.name
    }
    if (this.hasUploadButtonTarget) {
      this.uploadButtonTarget.classList.remove("hidden")
    }
    this._clearStatus()
  }

  _setUploading(loading) {
    if (this.hasUploadButtonTarget) {
      this.uploadButtonTarget.disabled = loading
      this.uploadButtonTarget.textContent = loading ? "Analysing..." : "Upload & Analyse"
    }
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.toggle("pointer-events-none", loading)
      this.dropZoneTarget.classList.toggle("opacity-50", loading)
    }
  }

  _showStatus(message, type) {
    if (!this.hasStatusMessageTarget) return
    const colors = {
      info: "text-blue-700 bg-blue-50 border-blue-200",
      success: "text-green-700 bg-green-50 border-green-200",
      error: "text-red-700 bg-red-50 border-red-200"
    }
    this.statusMessageTarget.className = `mt-3 p-3 rounded-lg border text-sm ${colors[type] || colors.info}`
    this.statusMessageTarget.textContent = message
    this.statusMessageTarget.classList.remove("hidden")
  }

  _clearStatus() {
    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.classList.add("hidden")
      this.statusMessageTarget.textContent = ""
    }
  }

  _renderExtractedData(data) {
    if (!this.hasExtractedDataTarget) return

    const labels = {
      current_role: "Current Role",
      location: "Location",
      experience_years: "Experience",
      seniority_level: "Seniority Level",
      skills_technical: "Technical Skills",
      skills_certifications: "Certifications",
      education: "Education",
      industry: "Industry",
      current_company_type: "Company Type"
    }

    const cards = Object.entries(data)
      .filter(([, value]) => value && (!Array.isArray(value) || value.length > 0))
      .map(([field, value]) => {
        const displayValue = Array.isArray(value) ? value.join(", ") : String(value)
        const label = labels[field] || field

        return `
          <div data-field-card class="flex items-start justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div class="flex-1 min-w-0">
              <div class="text-xs font-medium text-gray-500 uppercase tracking-wide">${this._escapeHtml(label)}</div>
              <div data-field-display class="mt-1 text-sm text-gray-900">${this._escapeHtml(displayValue)}</div>
              <div data-field-edit class="hidden mt-1">
                <input type="text" value="${this._escapeAttr(displayValue)}"
                       class="w-full text-sm border border-gray-300 rounded px-2 py-1 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 focus:outline-none">
              </div>
            </div>
            <div class="flex gap-1 ml-2 flex-shrink-0">
              <button data-action="click->salary-lab-cv-upload#editField"
                      data-field="${this._escapeAttr(field)}"
                      class="text-gray-400 hover:text-cyan-600 p-1" title="Edit">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
                </svg>
              </button>
              <button data-action="click->salary-lab-cv-upload#removeField"
                      data-field="${this._escapeAttr(field)}"
                      class="text-gray-400 hover:text-red-600 p-1" title="Remove">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        `
      })
      .join("")

    this.extractedDataTarget.innerHTML = cards
  }

  _escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  _escapeAttr(str) {
    return String(str).replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
